import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  chatWidgetApiURL,
  chatWidgetScriptURL,
  chatWidgetSupportApiURL,
  chatWidgetTicketCreateURL,
} from '../utils/Urls'

let scriptLoadPromise = null

function loadWidgetScript() {
  if (scriptLoadPromise) return scriptLoadPromise
  scriptLoadPromise = new Promise((resolve, reject) => {
    if (customElements.get('luma-chat-widget')) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = chatWidgetScriptURL
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('chat widget script failed to load'))
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

// The widget persists chat history to localStorage keyed by (among other
// things) its `storage-scope` attribute -- an unset/constant scope means an
// anonymous visitor's conversation survives a refresh forever. We want the
// opposite split: signed-in sub-brokers get real persistence tied to their
// account; anonymous chat is session-only and a refresh should start clean.
// A fresh random scope on every anonymous mount guarantees the widget can
// never find a previous conversation under it, which is a full reset without
// needing to know or touch its storage internals directly.
function randomScope() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function storageScopeFor(authenticated) {
  if (!authenticated) return randomScope()
  const subbrokerCode = localStorage.getItem('subbroker_code')
  return subbrokerCode ? `sb-${subbrokerCode}` : 'sb-authenticated'
}

// Floating AI support widget, mounted once at the app root so it's present on
// every page -- signed in or not. The AI chat itself (api-url/support-api-url)
// talks straight to the shared chat-agent service, no identity needed there,
// same as PocketX's own embed. Escalating to a human normally routes through
// this app's own backend (ticket-create-url), which attaches the sub-broker's
// session-verified identity server-side -- but only once `authenticated` is
// true. Before login, ticket-create-url is left unset so the widget falls
// back to creating an anonymous ticket directly against chat-agent (same
// "Not signed in" pattern PocketX's own widget already uses), rather than
// hitting a 401 against an endpoint that requires a session that doesn't
// exist yet.
export default function SupportChatWidget({ authenticated }) {
  const containerRef = useRef(null)
  const widgetRef = useRef(null)
  // Latest `authenticated` value, readable from the async mount callback below
  // -- widget creation is async (script load), so if login/logout happens in
  // that window the closed-over `authenticated` param would otherwise be stale.
  const authenticatedRef = useRef(authenticated)
  authenticatedRef.current = authenticated
  // Tracks which `authenticated` value is currently reflected on the widget's
  // attributes, so the sync effect below only acts on a REAL change and never
  // depends on effect-firing order (creation is async and can race with it).
  const appliedAuthRef = useRef(null)
  const { pathname } = useLocation()
  // A shared proposal link is opened by an investment client, not a
  // sub-broker -- hide (not unmount, so the widget/conversation state isn't
  // destroyed if someone navigates back) the sub-broker-branded assistant there.
  const hidden = pathname.startsWith('/proposals/share/')

  useEffect(() => {
    let cancelled = false
    loadWidgetScript()
      .then(() => {
        if (cancelled || !containerRef.current) return
        if (containerRef.current.querySelector('luma-chat-widget')) return
        const widget = document.createElement('luma-chat-widget')
        widget.setAttribute('agent-name', 'ATS Assistant')
        widget.setAttribute('agent-role', 'Sub-Broker Portal support')
        widget.setAttribute('assistant-mode', 'subbroker-support')
        // Matches the ATS brand green used across login/signup (SubbrokerSignup.css
        // --green) and the portal's primary action buttons, rather than the
        // widget's default purple -- the widget derives its whole palette
        // (including readable text contrast) from this one value.
        widget.setAttribute('accent-color', '#0d6b45')
        widget.setAttribute('compact-launcher', '')
        widget.setAttribute('api-url', chatWidgetApiURL)
        widget.setAttribute('support-api-url', chatWidgetSupportApiURL)
        widget.setAttribute('launcher-label', 'Chat with us')
        widget.setAttribute('human-action-label', 'Talk to support')
        widget.setAttribute('greeting', "Hi! I'm the ATS Assistant for the Sub-Broker Portal. How can I help today?")
        // Pipe-separated, widget.js caps this at 4. Kept to real, common
        // Sub-Broker Portal questions instead of the generic e-commerce
        // defaults ("Report a product issue" etc.) that don't fit this product.
        widget.setAttribute('quick-actions', [
          'How do I request a client payout?',
          'Why is Live Positions showing an error?',
          'How do I pledge or unpledge holdings?',
          'How do I raise a support ticket?',
        ].join('|'))
        // Read the ref, not the closed-over param: covers the (rare, but
        // possible) case where auth state changed while the script was loading.
        const currentAuth = authenticatedRef.current
        widget.setAttribute('storage-scope', storageScopeFor(currentAuth))
        if (currentAuth) widget.setAttribute('ticket-create-url', chatWidgetTicketCreateURL)
        appliedAuthRef.current = currentAuth
        containerRef.current.appendChild(widget)
        widgetRef.current = widget
      })
      .catch((error) => {
        // A missing/blocked widget script must never break the portal itself.
        console.warn('Support chat widget unavailable:', error.message)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Login/logout can happen without a page reload (OTP login, logout button),
  // so keep the identity-proxy attribute AND the storage scope in sync after
  // the widget already exists -- switching storage-scope makes the widget
  // start a fresh conversation under the new (persistent-if-authenticated,
  // random-if-not) key. Only acts when `authenticated` actually differs from
  // what's currently applied, so it's a no-op both before the widget exists
  // and on the render where the mount effect already applied this same value.
  useEffect(() => {
    const widget = widgetRef.current
    if (!widget || appliedAuthRef.current === authenticated) return
    if (authenticated) widget.setAttribute('ticket-create-url', chatWidgetTicketCreateURL)
    else widget.removeAttribute('ticket-create-url')
    widget.setAttribute('storage-scope', storageScopeFor(authenticated))
    appliedAuthRef.current = authenticated
  }, [authenticated])

  return <div ref={containerRef} style={hidden ? { display: 'none' } : undefined} />
}
